import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Input, Button, Checkbox, Typography } from "@material-tailwind/react";
import {
  EyeSlashIcon,
  EyeIcon,
  DocumentArrowUpIcon,
} from "@heroicons/react/24/outline";
import { DocumentPlusIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import {
  CountryOption,
  CountrySingleValue,
} from "@/components/CountrySelectComponents";
import {
  fetchCity,
  fetchCountry,
  fetchCountryMobileCode,
  getInternalUser,
  setSelectedInternalUser,
  storeDeliveryPartnerDetails,
  updateDeliveryPartnerDetails,
} from "@/store/slices/internalUsersSlice";
import FilePreview from "@/components/FilePreview";
import ShowLoader from "@/components/Loader";
import Toaster from "@/components/Toaster";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import ScrollToTop from "@/components/ScrollToTop";
import { deleteOtherDocument } from "@/store/slices/customerSlice";
import Swal from "sweetalert2";

const PAGE_URL = "delivery-partner";
const TYPE = "Delivery Partner";

const typeLabelMap = {
  "Delivery Partner": "delivery_partner",
};

/* ---------------- COMMON REGEX (Matching Sign-Up) ---------------- */
const noHtmlRegex = /^(?!.*<[^>]+>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;
const noLeadingSpaceRegex = /^(?!\s).*/;

/** Same pattern as add-internal-users: edit allows existing URL until removed. */
const profileImageRemoved = { profile_image: false }; // This seems unused in the file's logic, but keeping it as is.

/* ---------------- COMMON STRING SECURITY ---------------- */
const secureString = (t) =>
  Yup.string()
    .transform((value) => (value === null ? "" : value))
    .trim()
    .matches(noLeadingSpaceRegex, t("validations.no_leading_spaces"))
    .matches(noHtmlRegex, t("validations.no_html_tags"))
    .matches(noCodeRegex, t("validations.no_code_content"))
    .matches(noConsecutiveSpecialChars, t("validations.no_consecutive_special_chars"))
    .matches(noLeadingTrailingPunct, t("validations.no_leading_trailing_punctuation"));

/* ---------------- FILE VALIDATION HELPER ---------------- */
const fileValidation = (t, label, required = true) =>
  Yup.mixed()
    .nullable()
    .test("required", t("validations.field_required", { field: label }), (value) => {
      if (!required) return true;
      return value instanceof File || (typeof value === "string" && value.length > 0);
    })
    .test("fileSize", t("validations.file_size_max", { size: "5MB" }), (value) => {
      if (!value || typeof value === "string") return true;
      return value.size <= 5 * 1024 * 1024;
    })
    .test("fileType", t("validations.file_type_media"), (value) => {
      if (!value || typeof value === "string") return true;
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
      return allowedTypes.includes(value.type);
    });

function profileImageYup(t, isEditMode) {
  return Yup.mixed()
    .nullable()
    .notRequired()
    .test("fileType", t("validations.invalid_file_type"), (value) => {
      if (!value || typeof value === "string") return true;
      return value.type.startsWith("image/");
    });
}

const createBaseSchema = (t) => ({
  /* -------- NAME -------- */
  first_name: Yup.string()
    .transform((value) => (value === null ? "" : value))
    .required(t("validations.first_name_required"))
    .concat(secureString(t))
    .min(2, t("validations.first_name_min_max", { min: 2, max: 50 }))
    .max(50, t("validations.first_name_min_max", { min: 2, max: 50 }))
    .matches(/^[a-zA-Z\s]+$/, t("validations.only_alphabets")),
  last_name: Yup.string()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .notRequired()
    .when([], {
      is: (val) => val != null,
      then: (schema) =>
        schema
          .concat(secureString(t))
          .min(2, t("validations.last_name_min_max", { min: 2, max: 50 }))
          .max(50, t("validations.last_name_min_max", { min: 2, max: 50 }))
          .matches(/^[a-zA-Z\s]+$/, t("validations.only_alphabets")),
      otherwise: (schema) => schema.notRequired(),
    }),

  gender: Yup.string()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .required(t("validations.gender_required"))
    .oneOf(["Male", "Female", "Other"], t("validations.gender_invalid")),

  /* -------- CONTACT -------- */
  country_code: Yup.string()
    .trim()
    .required(t("validations.country_code_required"))
    .matches(/^\+[1-9][0-9]{0,3}$/, t("validations.country_code_invalid")),
  mobile_number: Yup.string()
    .trim()
    .required(t("validations.mobile_number_required"))
    .matches(noLeadingSpaceRegex, t("validations.no_leading_spaces"))
    .matches(/^[0-9]+$/, t("validations.mobile_number_only_digits"))
    .min(8, t("validations.mobile_number_min_max", { min: 8, max: 15 }))
    .max(15, t("validations.mobile_number_min_max", { min: 8, max: 15 }))
    .test("not-all-zeros", t("validations.mobile_number_all_zeros"), (value) =>
      value ? !/^0+$/.test(value) : false
    ),
  email: Yup.string()
    .trim()
    .lowercase()
    .required(t("validations.email_required"))
    .matches(noLeadingSpaceRegex, t("validations.no_leading_spaces"))
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      t("validations.email_invalid_format")
    )
    .max(100, t("validations.email_max", { max: 100 }))
    .matches(noHtmlRegex, t("validations.invalid_chars")),

  /* -------- ADDRESS -------- */
  permanent_address: secureString(t)
    .required(t("validations.address_required"))
    .min(10, t("validations.address_min_max", { min: 10, max: 100 }))
    .max(100, t("validations.address_min_max", { min: 10, max: 100 })),

  /* -------- RESIDENCE -------- */
  residence_id: secureString(t)
    .required(t("validations.residence_id_required"))
    .min(2, t("validations.residence_id_min_max", { min: 2, max: 30 }))
    .max(30, t("validations.residence_id_min_max", { min: 2, max: 30 })),

  /* -------- DRIVING LICENCE -------- */
  // driving_licence: secureString()
  //   .required("Driving licence number is required")
  //   .min(5, "Driving licence number can't be less than 5 characters")
  //   .max(20, "Driving licence number can't be more than 20 characters")
  //   .matches(/^[0-9A-Z]+$/i, "Only letters and numbers allowed"),

  /* -------- VEHICLE -------- */
  vehicle_number: secureString(t)
    .notRequired()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .min(4, t("validations.vehicle_number_min_max", { min: 4, max: 20 }))
    .max(20, t("validations.vehicle_number_min_max", { min: 4, max: 20 })),

  /* -------- INSURANCE -------- */
  // insurance: secureString()
  //   .notRequired()
  //   .nullable()
  //   .transform((v) => (v === "" ? null : v))
  //   .min(5, "Insurance policy number must be at least 5 characters")
  //   .max(30, "Insurance policy number must be less than 30 characters"),

  /* -------- LOCATION -------- */
  country_id: Yup.string().required(t("validations.country_required")),
  city_id: Yup.string().required(t("validations.city_required")),
});

// const createPartnerSchema = Yup.object().shape({
//   ...baseSchema,
//   password: Yup.string()
//     .required("Password is required")
//     .min(8, "Password must be at least 8 characters")
//     .max(15, "Password cannot exceed 15 characters")
//     .matches(noLeadingSpaceRegex, "Leading spaces are not allowed")
//     .matches(/[A-Z]/, "Must contain at least one uppercase letter")
//     .matches(/[a-z]/, "Must contain at least one lowercase letter")
//     .matches(/[0-9]/, "Must contain at least one number")
//     .matches(/[@$!%*?&#]/, "Must contain at least one special character")
//     .matches(noHtmlRegex, "Invalid characters detected"),
//   password_confirmation: Yup.string()
//     .required("Confirm password is required")
//     .oneOf([Yup.ref("password")], "Password and Confirm Password must match"),
//   profile_image: profileImageYup(false),
//   residence_image: fileValidation("Residence ID file", true),
//   driving_licence_image: fileValidation("Driving licence file", true),
//   insurance_image: fileValidation("Insurance file", false),
// });

// const editValidationSchema = Yup.object().shape({
//   ...baseSchema,
//   password: Yup.string()
//     .nullable()
//     .transform((v) => (v == null || String(v).trim() === "" ? undefined : v))
//     .notRequired()
//     .min(8, "Password must be at least 8 characters")
//     .max(15, "Password cannot exceed 15 characters")
//     .matches(/[A-Z]/, "Must contain at least one uppercase letter")
//     .matches(/[a-z]/, "Must contain at least one lowercase letter")
//     .matches(/[0-9]/, "Must contain at least one number")
//     .matches(/[@$!%*?&#]/, "Must contain at least one special character"),
//   password_confirmation: Yup.string().when("password", {
//     is: (val) => val != null && String(val).length > 0,
//     then: (schema) =>
//       schema
//         .required("Confirm password is required")
//         .oneOf([Yup.ref("password")], "Password and Confirm Password must match"),
//     otherwise: (schema) => schema.notRequired().nullable(),
//   }),
//   profile_image: profileImageYup(true),
//   residence_image: fileValidation("Residence ID file", false),
//   driving_licence_image: fileValidation("Driving licence file", false),
//   insurance_image: fileValidation("Insurance file", false),
// });

/* ---------------- PASSWORD VALIDATOR ---------------- */
const passwordValidationLogic = (value, context) => {
  if (!value) return true;
  const { t } = context.options.context;
  let errors = [];
  if (value.length < 8) errors.push(t("validations.password_min", { min: 8 }));
  if (value.length > 15) errors.push(t("validations.password_max", { max: 15 }));
  if (!/[A-Z]/.test(value)) errors.push(t("validations.password_uppercase"));
  if (!/[a-z]/.test(value)) errors.push(t("validations.password_lowercase"));
  if (!/[0-9]/.test(value)) errors.push(t("validations.password_number"));
  if (!/[@$!%*?&#]/.test(value)) errors.push(t("validations.password_special_char"));
  if (!noHtmlRegex.test(value)) errors.push(t("validations.invalid_chars"));

  if (errors.length > 0) {
    return context.createError({
      message: errors.map(e => `• ${e}`).join("\n"),
    });
  }
  return true;
};

const createPartnerSchema = (t) => Yup.object().shape({
  ...createBaseSchema(t),
  password: Yup.string()
    .required(t("validations.password_required"))
    .test("password-validation", function (value) {
      return passwordValidationLogic(value, this);
    }),
  password_confirmation: Yup.string()
    .required(t("validations.password_confirmation_required"))
    .oneOf([Yup.ref("password")], t("validations.password_match")),
  profile_image: profileImageYup(t, false),
  residence_image: fileValidation(t, t("residence_id_file"), true),
  driving_licence_image: fileValidation(t, t("driving_license_file"), true),
  insurance_image: fileValidation(t, t("insurance_file"), false),
  vehicle_certificate_image: fileValidation(t, t("vehicle_certificate_file"), false),
});

const editValidationSchema = (t) => Yup.object().shape({
  ...createBaseSchema(t),
  password: Yup.string()
    .nullable()
    .transform((v) => (v == null || String(v).trim() === "" ? undefined : v))
    .notRequired()
    .test("password-validation", function (value) {
      return passwordValidationLogic(value, this);
    }),
  password_confirmation: Yup.string().when("password", {
    is: (val) => val != null && String(val).length > 0,
    then: (schema) =>
      schema
        .required(t("validations.password_confirmation_required"))
        .oneOf([Yup.ref("password")], t("validations.password_match")),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  profile_image: profileImageYup(t, true),
  residence_image: fileValidation(t, t("residence_id_file"), false),
  driving_licence_image: fileValidation(t, t("driving_license_file"), false),
  insurance_image: fileValidation(t, t("insurance_file"), false),
  vehicle_certificate_image: fileValidation(t, t("vehicle_certificate_file"), false),
  other_documents: Yup
    .mixed()
    .nullable()
    .test("fileType", t("validations.file_type_media"), (value) => {
      if (!value) return true;
      const files = Array.isArray(value) ? value : [value];
      return files.every((file) => {
        if (typeof file === "string") return true;

        if (file?.file && typeof file.file === "string") return true;

        if (file instanceof File) {
          return file.type.startsWith("image/") || file.type === "application/pdf";
        }

        return false;
      });
    })
    .test("fileCount", t("validations.file_count_max", { count: 10 }), (value) => {
      if (!value) return true;
      const files = Array.isArray(value) ? value : [value];
      return files.length <= 10;
    }),
  other_documents_detail: Yup
    .string()
    .nullable()
    .test(
      "not-only-spaces",
      t("validations.no_spaces_only"),
      (value) => {
        if (value === null || value === undefined) return true; // allow null
        if (value === "") return true; // allow empty
        return value.trim().length > 0; // reject only-space values
      }
    )
    .matches(noHtmlRegex, t("validations.no_html_tags"))
    .matches(noCodeRegex, t("validations.no_code_content"))
    .matches(noConsecutiveSpecialChars, t("validations.no_consecutive_special_chars"))
    .matches(noLeadingTrailingPunct, t("validations.no_leading_trailing_punctuation"))
    .max(100, t("validations.other_docs_detail_max", { max: 100 }))
});

const defaultValues = {
  first_name: null,
  last_name: null,
  country_code: null,
  mobile_number: null,
  email: null,
  gender: null,
  password: null,
  password_confirmation: null,
  profile_image: null,
  permanent_address: null,
  residence_id: null,
  residence_image: null,
  driving_licence: null,
  driving_licence_image: null,
  vehicle_number: null,
  vehicle_certificate_image: null,
  insurance: null,
  insurance_image: null,
  agree_terms: true,
  country_id: null,
  city_id: null,
  other_documents: [],
  other_documents_detail: null
};

export default function AddDeliveryPartners() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const { t } = useTranslation("internal-user");
  const typeLabel = typeLabelMap[TYPE] || TYPE;
  const location = useLocation();
  const isEdit = Boolean(
    editId && location.pathname.includes("/delivery-partner/edit/")
  );

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isHydrating, setIsHydrating] = useState(isEdit);

  const { mobile_country_code, cities, countries } = useSelector(
    (state) => state.internalUser
  );
  const loading = useSelector((state) => state.internalUser.loading);
  const selected = useSelector((state) => state.internalUser.selected);

  const residenceRef = useRef(null);
  const licenseRef = useRef(null);
  const vehicleCertificateRef = useRef(null);
  const insuranceRef = useRef(null);

  const resolverSchema = useMemo(
    () => (isEdit ? editValidationSchema(t) : createPartnerSchema(t)),
    [isEdit, t]
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    setError,
    reset,
  } = useForm({
    defaultValues,
    resolver: yupResolver(resolverSchema),
    context: { t },
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    dispatch(fetchCountryMobileCode());
    dispatch(fetchCountry());
  }, [dispatch]);

  useEffect(() => {
    if (!isEdit || !editId) return;
    if (selected && String(selected.id) === String(editId)) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await dispatch(
          getInternalUser({
            type: TYPE,
            activetab: "all",
            page: 1,
            perPage: 200,
            search: "",
            filter: "",
            city: "",
          })
        ).unwrap();
        const user = res?.users?.find((u) => String(u.id) === String(editId));
        if (!cancelled && user) {
          dispatch(setSelectedInternalUser(user));
        } else if (!cancelled) {
          setIsHydrating(false);
          Toaster.error(t("delivery_partner_not_found"));
        }
      } catch {
        if (!cancelled) {
          setIsHydrating(false);
          Toaster.error(t("delivery_partner_load_error"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, selected, dispatch]);

  useEffect(() => {
    if (!isEdit || !editId || !selected || String(selected.id) !== String(editId)) {
      if (!isEdit) setIsHydrating(false);
      return;
    }

    setIsHydrating(true);
    profileImageRemoved.profile_image = false;
    const countryId = selected.country_id;
    const run = async () => {
      try {
        if (countryId) {
          await dispatch(fetchCity(countryId)).unwrap();
        }
      } catch {
        /* ignore */
      }

      const first = selected.first_name || "";
      const last = selected.last_name || "";
      let firstName = first;
      let lastName = last;
      if (!lastName && first.includes(" ")) {
        const parts = first.trim().split(/\s+/);
        lastName = parts.pop() || "";
        firstName = parts.join(" ") || first;
      }

      reset({
        ...defaultValues,
        first_name: firstName,
        last_name: lastName,
        country_code: selected.country_code,
        mobile_number: selected.mobile_number,
        email: selected.email,
        gender: selected.gender,
        password: "",
        password_confirmation: "",
        profile_image: selected.profile_image ?? null,
        permanent_address: selected.permanent_address ?? "",
        residence_id: selected.residence_id ?? "",
        residence_image: selected.residence_image || selected.id_proof || null,
        driving_licence: selected.driving_licence ?? "",
        driving_licence_image: selected.driving_licence_image || null,
        vehicle_number: selected.vehicle_number ?? "",
        vehicle_certificate_image: selected.vehicle_certificate_image || null,
        insurance: selected.insurance ?? "",
        insurance_image: selected.insurance_image || null,
        agree_terms: true,
        country_id: selected.country_id,
        city_id: selected.city_id,
        other_documents: selected?.other_documents || [],
        other_documents_detail: selected?.other_documents_detail || null
      });
      setIsHydrating(false);
    };
    run();
  }, [isEdit, editId, selected, dispatch, reset]);

  const handleNoLeadingSpace = (e, fieldName) => {
    const value = e.target.value;
    if (value.startsWith(" ")) {
      setValue(fieldName, value.trimStart());
    }
  };

  const onSubmit = (data) => {
    if (isEdit) {
      if (!selected?.id) {
        Toaster.error(t("no_partner_loaded"));
        return;
      }
      const body = { ...data, id: String(selected.id) };
      if (!data.password?.trim()) {
        delete body.password;
        delete body.password_confirmation;
      }
      dispatch(updateDeliveryPartnerDetails(body))
        .unwrap()
        .then((res) => {
          if (res?.status) {
            Toaster.success(res?.message || t("delivery_partner_updated_success"));
            navigate(`/dashboard/${PAGE_URL}`);
          } else {
            Toaster.error(res?.message || t("update_failed"));
            if (res?.data?.field) {
              setError(res.data.field, {
                type: "manual",
                message: res?.data?.message,
              });
            }
          }
        })
        .catch((err) => {
          Toaster.error(err?.message || t("update_failed"));
        });
      return;
    }

    dispatch(storeDeliveryPartnerDetails(data))
      .unwrap()
      .then((res) => {
        if (res?.status) {
          Toaster.success(res?.message || t("delivery_partner_created_success"));
          navigate(`/dashboard/${PAGE_URL}`);
        } else {
          Toaster.error(res?.message || t("delivery_partner_create_error"));
          if (res?.data?.field) {
            setError(res.data.field, {
              type: "manual",
              message: res?.data?.message,
            });
          }
        }
      })
      .catch((err) => {
        Toaster.error(err?.message || t("delivery_partner_create_error"));
      });
  };

  const other_documents = watch('other_documents') || [];
  const insurance_image = watch("insurance_image");
  const vehicle_certificate_image = watch("vehicle_certificate_image");
  const driving_licence_image = watch("driving_licence_image");
  const residence_image = watch("residence_image");

  const profileImage = watch("profile_image");

  const removeProfileImage = () => {
    setValue("profile_image", null, { shouldValidate: true });
    if (isEdit) {
      profileImageRemoved.profile_image = true;
    }
  };

  const handleFileChange = (e, field) => {
    const files = Array.from(e.target.files);

    if (field === "other_documents") {
      const existingDocs = watch("other_documents") || [];

      setValue("other_documents", [...existingDocs, ...files], { shouldValidate: true });
    } else {
      setValue(field, files[0], { shouldValidate: true });
    }
  };

  const removeDocument = (index, documentId = null) => {
    const updated = [...other_documents];
    const removedDoc = updated.splice(index, 1);
    setValue("other_documents", updated);

    if (documentId) {
      Swal.fire({
        title: t("are_you_sure"),
        text: t("cant_revert"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: t("yes_delete_it")
      }).then((result) => {
        if (result.isConfirmed) {
          dispatch(deleteOtherDocument(documentId)).unwrap().then(() => {
            Toaster.success(t("other_doc_deleted_success"));
          }).catch((error) => {
            Toaster.error(t("failed_to_delete_doc"));
            setValue("other_documents", [...updated, removedDoc[0]]);
          });
        } else {
          setValue("other_documents", [...updated, removedDoc[0]]);
        }
      });
    }
  }

  const inputBaseStyles = "!border-t-[#CFD2D9] focus:!border-t-[#0A5D38] placeholder:text-[#2F302C61] shadow-none";
  const labelProps = { className: "before:content-none after:content-none" };
  const showLoader = loading || (isEdit && isHydrating);

  return (
    <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {showLoader && <ShowLoader />}
      <ScrollToTop />

      <div className="sticky top-0 z-30 mt-1 p-5 bg-blue-gray-f5 rounded-lg">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2">
          <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
            {isEdit ? t("edit_delivery_partner") : t("add_new_delivery_partner")}
          </Typography>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outlined" onClick={() => navigate(`/dashboard/${PAGE_URL}`)} className="rounded-lg">
              {t("cancel")}
            </Button>
            <Button type="submit" form="delivery-partner-admin-form" disabled={loading || (isEdit && isHydrating)} className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material">
              {loading ? t("saving") : isEdit ? t("update") : t("save")}
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Link to={`/dashboard/${PAGE_URL}`} className="flex items-center text-blue-600 hover:text-blue-800">
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            {t("back")}
          </Link>
        </div>
      </div>

      <div className="border border-blue-gray-100 rounded-lg p-5 sm:p-8 bg-white">
        <div className="mb-5 pb-2 border-b border-blue-gray-200">
          <Typography variant="h4" color="blue-gray">{t("basic_user_details")}</Typography>
        </div>
        <form id="delivery-partner-admin-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-1.5 font-lato">
              <Typography className="font-lato text-[#2F302C] text-[16px]">{t("first_name")} <span className="text-[#FF0000]">*</span></Typography>
              <Input size="lg" placeholder={t("enter_first_name")} {...register("first_name", { onChange: (e) => handleNoLeadingSpace(e, "first_name") })} error={!!errors.first_name} className={inputBaseStyles} labelProps={labelProps} />
              {errors.first_name && <span className="text-[#FF0000] text-xs">{errors.first_name.message}</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Typography variant="small" className="font-lato text-[#2F302C] text-[16px]">{t("last_name")} </Typography>
              <Input size="lg" placeholder={t("enter_last_name")} {...register("last_name", { onChange: (e) => handleNoLeadingSpace(e, "last_name") })} error={!!errors.last_name} className={inputBaseStyles} labelProps={labelProps} />
              {errors.last_name && <span className="text-[#FF0000] text-xs">{errors.last_name.message}</span>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-1">
              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px] font-medium mb-1"
              >
                {t("profile_photo")}
              </Typography>

              <div className="flex flex-wrap items-center gap-2">
                {!profileImage ? (
                  <div className="w-16 h-16 flex items-center justify-center rounded-full border-2 border-dashed border-gray-300 shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-6 h-6 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zM4 20c0-2.67 5.33-4 8-4s8 1.33 8 4"
                      />
                    </svg>
                  </div>
                ) : (
                  <div className="relative w-20 h-20 shrink-0 overflow-visible">
                    <div className="w-full h-full rounded-full overflow-hidden border-2 border-gray-300 bg-gray-50 flex items-center justify-center">
                      <FilePreview
                        file={profileImage}
                        onRemove={removeProfileImage}
                        containerClass="w-full h-full flex items-center justify-center"
                      />
                    </div>
                  </div>
                )}

                <input
                  id="dpAdminProfilePhoto"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) =>
                    setValue("profile_image", e.target.files?.[0] || null, {
                      shouldValidate: true,
                    })
                  }
                />

                <label
                  htmlFor="dpAdminProfilePhoto"
                  className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                >
                  <DocumentPlusIcon className="h-4 w-4 text-green-800" />
                  {t("choose_file")}
                </label>

                <Typography className="text-xs text-gray-400 w-full sm:w-auto">
                  {t("jpg_png_upto_5mb")}
                </Typography>
              </div>

              {errors.profile_image && (
                <span className="text-[#FF0000] text-xs mt-1">
                  {errors.profile_image.message}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Typography
                variant="small"
                className="font-lato text-[#2F302C] text-[16px]"
              >
                {t("gender")} <span className="text-[#FF0000]">*</span>
              </Typography>

              <Select
                placeholder={t("select_gender")}
                options={[
                  { value: "Male", label: t("male") },
                  { value: "Female", label: t("female") },
                  { value: "Other", label: t("other") },
                ]}
                onChange={(option) => {
                  setValue("gender", option?.value, {
                    shouldValidate: true,
                  });
                }}
                value={
                  [
                    { value: "Male", label: t("male") },
                    { value: "Female", label: t("female") },
                    { value: "Other", label: t("other") },
                  ].find((opt) => opt.value === watch("gender")) || null
                }
                isClearable
                styles={{
                  control: (base, state) => ({
                    ...base,
                    height: "44px",
                    minHeight: "44px",
                    borderColor: errors.gender ? "#FF0000" : "#CFD2D9",
                    boxShadow: state.isFocused ? "0 0 0 1px #333" : "none",
                    "&:hover": {
                      borderColor: "#90A4AE",
                    },
                  }),
                }}
              />

              {errors.gender && (
                <span className="text-[#FF0000] text-xs">
                  {errors.gender.message}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-1.5 font-lato">
              <Typography className="font-lato text-[#2F302C] text-[16px]">{t("phone_number_whatsapp")} <span className="text-[#FF0000]">*</span></Typography>
              <div className="relative flex w-full">
                <Select placeholder={t("select_code")} onChange={(country_code) => setValue("country_code", country_code.value, { shouldValidate: true })} value={mobile_country_code?.find((opt) => opt.value === watch("country_code")) || null} styles={{ control: (base, state) => ({ ...base, height: "44px", minHeight: "44px", borderColor: errors.country_code ? "#FF0000" : "#CFD2D9", boxShadow: state.isFocused ? "0 0 0 1px #333" : "none", "&:hover": { borderColor: "#90A4AE" } }) }} className="min-w-[150px]" options={mobile_country_code} components={{ Option: CountryOption, SingleValue: CountrySingleValue }} />
                <Input size="lg" placeholder={t("enter_phone_number_placeholder")} {...register("mobile_number", { onChange: (e) => handleNoLeadingSpace(e, "mobile_number") })} error={!!errors.mobile_number} className={`${inputBaseStyles} rounded-l-none !border-t-blue-gray-200 focus:!border-amber-500`} labelProps={labelProps} containerProps={{ className: "min-w-0" }} />
              </div>
              {(errors.mobile_number || errors.country_code) && <span className="text-[#FF0000] text-xs">{errors.mobile_number?.message || errors.country_code?.message}</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Typography variant="small" className="font-lato text-[#2F302C] text-[16px]">{t("email_address")} <span className="text-[#FF0000]">*</span></Typography>
              <Input size="lg" placeholder={t("enter_email_address")} {...register("email", { onChange: (e) => handleNoLeadingSpace(e, "email") })} error={!!errors.email} className={inputBaseStyles} labelProps={labelProps} />
              {errors.email && <span className="text-[#FF0000] text-xs">{errors.email.message}</span>}
            </div>
          </div>
          {!isEdit && <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-1.5">
                <Typography variant="small" className="font-lato text-[#2F302C] text-[16px]">{t("password")}
                  <span className="text-[#FF0000]">*</span>
                </Typography>

                <Input type={showPassword ? "text" : "password"} size="lg" placeholder={isEdit ? t("password_placeholder_edit") : t("password_placeholder_create")} {...register("password", { onChange: (e) => handleNoLeadingSpace(e, "password") })} error={!!errors.password} className={inputBaseStyles} labelProps={labelProps} icon={showPassword ? <EyeIcon className="h-5 w-5 cursor-pointer" onClick={() => setShowPassword(false)} /> : <EyeSlashIcon className="h-5 w-5 cursor-pointer" onClick={() => setShowPassword(true)} />} />
                {errors.password && <span className="text-[#FF0000] text-xs">{errors.password.message}</span>}
              </div>
              <div className="flex flex-col gap-1.5">
                <Typography variant="small" className="font-lato text-[#2F302C] text-[16px]">{t("confirm_password")}
                  <span className="text-[#FF0000]">*</span>
                </Typography>
                <Input type={showConfirmPassword ? "text" : "password"} size="lg" placeholder={t("confirm_password_placeholder")} {...register("password_confirmation", { onChange: (e) => handleNoLeadingSpace(e, "password_confirmation") })} error={!!errors.password_confirmation} className={inputBaseStyles} labelProps={labelProps} icon={showConfirmPassword ? <EyeIcon className="h-5 w-5 cursor-pointer" onClick={() => setShowConfirmPassword(false)} /> : <EyeSlashIcon className="h-5 w-5 cursor-pointer" onClick={() => setShowConfirmPassword(true)} />} />
                {errors.password_confirmation && <span className="text-[#FF0000] text-xs">{errors.password_confirmation.message}</span>}
              </div>
            </div>
          </>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-1.5">
              <Typography variant="small" className="font-lato text-[#2F302C] text-[16px]">{t("country")} <span className="text-[#FF0000]">*</span></Typography>
              <Select placeholder={t("select_country")} onChange={(country) => { setValue("country_id", country?.value, { shouldValidate: true }); dispatch(fetchCity(country?.value)); }} error={Boolean(errors.country_id)} options={countries} value={countries?.find((opt) => opt.value === watch("country_id")) || null} isClearable noOptionsMessage={() => t("no_country_available")} />
              {errors.country_id && <span className="text-[#FF0000] text-xs">{errors.country_id.message}</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Typography variant="small" className="font-lato text-[#2F302C] text-[16px]">{t("city")} <span className="text-[#FF0000]">*</span></Typography>
              <Select placeholder={t("select_city")} onChange={(city) => setValue("city_id", city?.value, { shouldValidate: true })} error={Boolean(errors.city_id)} options={cities} value={cities?.find((opt) => opt.value === watch("city_id")) || null} isClearable noOptionsMessage={() => t("no_city_available")} />
              {errors.city_id && <span className="text-[#FF0000] text-xs">{errors.city_id.message}</span>}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Typography variant="small" className="font-lato text-[#2F302C] text-[16px]">{t("address")} <span className="text-[#FF0000]">*</span></Typography>
            <Input size="lg" placeholder={t("enter_address")} {...register("permanent_address", { onChange: (e) => handleNoLeadingSpace(e, "permanent_address") })} error={!!errors.permanent_address} className={inputBaseStyles} labelProps={labelProps} />
            {errors.permanent_address && <span className="text-[#FF0000] text-xs">{errors.permanent_address.message}</span>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-1.5 font-lato">
              <Typography className="font-lato text-[#2F302C] text-[16px]">{t("residence_id_copy")} <span className="text-[#FF0000]">*</span></Typography>
              <div className="flex h-11 italic">
                <Input size="lg" placeholder={t("enter_residence_id_or_iqama_id")} {...register("residence_id", { onChange: (e) => handleNoLeadingSpace(e, "residence_id") })} className={`${inputBaseStyles} !border-[#BDD9CD] rounded-r-none h-full focus:!border-amber-500`} containerProps={{ className: "min-w-0" }} labelProps={labelProps} />
                <Button type="button" onClick={() => residenceRef.current?.click()} className="flex flex-shrink-0 items-center gap-2 bg-[#065C3626] text-[#065C36] border border-dashed border-[#065C36] shadow-none rounded-l-none normal-case px-3 text-[13px] font-bold h-full">
                  <DocumentArrowUpIcon className="h-4 w-4 stroke-[2.5px]" /> {t("choose_file")}
                </Button>
                <input type="file" className="hidden" accept="image/*,application/pdf" ref={residenceRef} onChange={(e) => setValue("residence_image", e.target.files?.[0], { shouldValidate: true })} />
              </div>
              {(errors.residence_id || errors.residence_image) && <span className="text-[#FF0000] text-xs">{errors.residence_id?.message || errors.residence_image?.message}</span>}
              {residence_image && <div className="mt-3"><FilePreview file={residence_image} onRemove={() => setValue("residence_image", null, { shouldValidate: false })} isView={false} containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300" /></div>}
            </div>

            <div className="flex flex-col gap-1.5">
              <Typography variant="small" className="font-lato text-[#2F302C] text-[16px]">{t("driving_license")} <span className="text-[#FF0000]">*</span></Typography>
              <div className="flex h-11 italic">
                <Input size="lg" placeholder={t("upload_driving_license_copy")}
                  readOnly
                  // {...register("driving_licence", { onChange: (e) => handleNoLeadingSpace(e, "driving_licence") })}
                  className={`${inputBaseStyles} !border-[#BDD9CD] rounded-r-none h-full focus:!border-amber-500 cursor-not-allowed`}
                  containerProps={{ className: "min-w-0" }} labelProps={labelProps} />
                <Button type="button" onClick={() => licenseRef.current?.click()} className="flex flex-shrink-0 items-center gap-2 bg-[#065C3626] text-[#065C36] border border-dashed border-[#065C36] shadow-none rounded-l-none normal-case px-3 text-[13px] font-bold h-full">
                  <DocumentArrowUpIcon className="h-4 w-4" /> {t("choose_file")}
                </Button>
                <input type="file" className="hidden" accept="image/*,application/pdf" ref={licenseRef} onChange={(e) => setValue("driving_licence_image", e.target.files?.[0], { shouldValidate: true })} />
              </div>
              {(errors.driving_licence || errors.driving_licence_image) && <span className="text-[#FF0000] text-xs">{errors.driving_licence?.message || errors.driving_licence_image?.message}</span>}
              {driving_licence_image && <div className="mt-3"><FilePreview file={driving_licence_image} onRemove={() => setValue("driving_licence_image", null, { shouldValidate: false })} isView={false} containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300" /></div>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-1.5 font-lato">
              <Typography className="font-lato text-[#2F302C] text-[16px]">
                {t("vehicle_number")}
              </Typography>
              <div className="flex h-11 italic">
                <Input
                  size="lg"
                  placeholder={t("enter_vehicle_number")}
                  {...register("vehicle_number", {
                    onChange: (e) => handleNoLeadingSpace(e, "vehicle_number"),
                  })}
                  className={`${inputBaseStyles} !border-[#BDD9CD] rounded-r-none h-full focus:!border-amber-500`}
                  containerProps={{ className: "min-w-0" }}
                  labelProps={labelProps}
                />
                <Button
                  type="button"
                  onClick={() => vehicleCertificateRef.current?.click()}
                  className="flex flex-shrink-0 items-center gap-2 bg-[#065C3626] text-[#065C36] border border-dashed border-[#065C36] shadow-none rounded-l-none normal-case px-3 text-[13px] font-bold h-full"
                >
                  <DocumentArrowUpIcon className="h-4 w-4 stroke-[2.5px]" /> {t("choose_file")}
                </Button>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*,application/pdf"
                  ref={vehicleCertificateRef}
                  onChange={(e) =>
                    setValue("vehicle_certificate_image", e.target.files?.[0], {
                      shouldValidate: true,
                    })
                  }
                />
              </div>
              {(errors.vehicle_number || errors.vehicle_certificate_image) && (
                <span className="text-[#FF0000] text-xs">
                  {errors.vehicle_number?.message ||
                    errors.vehicle_certificate_image?.message}
                </span>
              )}
              {vehicle_certificate_image && (
                <div className="mt-3">
                  <FilePreview
                    file={vehicle_certificate_image}
                    onRemove={() =>
                      setValue("vehicle_certificate_image", null, {
                        shouldValidate: false,
                      })
                    }
                    isView={false}
                    containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Typography variant="small" className="font-lato text-[#2F302C] text-[16px]">{t("insurance")}</Typography>
              <div className="flex h-11 italic">
                <Input size="lg" placeholder={t("upload_insurance_copy")}
                  // {...register("insurance", { onChange: (e) => handleNoLeadingSpace(e, "insurance") })} 
                  readOnly
                  className={`${inputBaseStyles} !border-[#BDD9CD] rounded-r-none h-full focus:!border-amber-500 cursor-not-allowed`}
                  containerProps={{ className: "min-w-0" }} labelProps={labelProps} />
                <Button type="button" onClick={() => insuranceRef.current?.click()} className="flex flex-shrink-0 items-center gap-2 bg-[#065C3626] text-[#065C36] border border-dashed border-[#065C36] shadow-none rounded-l-none normal-case px-3 text-[13px] font-bold h-full">
                  <DocumentArrowUpIcon className="h-4 w-4" /> {t("choose_file")}
                </Button>
                <input type="file" className="hidden" accept="image/*,application/pdf" ref={insuranceRef} onChange={(e) => setValue("insurance_image", e.target.files?.[0], { shouldValidate: true })} />
              </div>
              {(errors.insurance || errors.insurance_image) && <span className="text-[#FF0000] text-xs">{errors.insurance?.message || errors.insurance_image?.message}</span>}
              {insurance_image && <div className="mt-3"><FilePreview file={insurance_image} onRemove={() => setValue("insurance_image", null, { shouldValidate: false })} isView={false} containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300" /></div>}
            </div>
          </div>
          <div className="flex flex-col gap-1.5 font-lato">
            <Typography className="font-lato text-[#2F302C] text-[16px]">
              {t("other_documents")}
            </Typography>

            <div className="flex h-11 italic">
              {/* Text Input */}
              <Input
                type="text"
                size="lg"
                placeholder={t("enter_other_document_details")}
                {...register("other_documents_detail")}
                className={`${inputBaseStyles} !border-[#BDD9CD] rounded-r-none h-full focus:!border-amber-500`}
                containerProps={{ className: "min-w-0" }}
                labelProps={{ className: "before:content-none after:content-none" }}
              />

              {/* Upload Button */}
              <Button
                type="button"
                onClick={() =>
                  document.getElementById("otherDocumentFile")?.click()
                }
                className="flex flex-shrink-0 items-center gap-2 bg-[#065C3626] text-[#065C36] border border-dashed border-[#065C36] shadow-none rounded-l-none normal-case px-3 text-[13px] font-bold h-full"
              >
                <DocumentArrowUpIcon className="h-4 w-4 stroke-[2.5px]" />
                {t("choose_file")}
              </Button>

              {/* Hidden Input */}
              <input
                id="otherDocumentFile"
                type="file"
                accept="image/*,application/pdf"
                multiple
                className="hidden"
                onChange={(e) => handleFileChange(e, "other_documents")}
              />
            </div>

            {/* Errors */}
            {(errors.other_documents_detail || errors?.other_documents) && (
              <span className="text-[#FF0000] text-xs">
                {errors.other_documents_detail?.message ||
                  errors.other_documents?.message ||
                  t("validations.invalid_file_type")}
              </span>
            )}

            {/* Preview */}
            {other_documents.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-6">
                {other_documents?.map((doc, index) => {
                  const isExisting = !!doc?.id;
                  const file = isExisting ? doc.file : doc;

                  return (
                    <FilePreview
                      key={index}
                      file={file}
                      onRemove={() =>
                        removeDocument(index, isExisting ? doc.id : "")
                      }
                      isView={false}
                      containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                      className="object-cover w-full h-full"
                    />
                  );
                })}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}